import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { GatewayApiClient, getHttpStatus } from '@evidentid/gateway-api-client';
import { GoAppConfig } from '@evidentid/tprm-portal-lib/models/go-app';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { VuexCommonEntityStatus } from '@/models/VuexCommonEntityStatus.model';
import {
    mutateCommonEntityFailState,
    mutateCommonEntityFinishState,
    mutateCommonEntityStartState,
} from '@/utils/vuex-common/commonEntityMutations';

interface GoAppSettingsRequirement {
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): GoAppSettingsRequirement {
    return store.$app;
}

interface SaveStatus {
    status: OperationStatus;
    // On failure this is the parsed rejection body (gateway client attaches httpStatus),
    // so the view can show the backend validation message inline.
    error: any;
}

interface GoAppSettingsState {
    goAppConfig: Record<string, VuexCommonEntityStatus<GoAppConfig>>;
    saveStatus: Record<string, SaveStatus>;
}

export const useGoAppSettingsStore = defineStore('goAppSettings', {
    state: (): GoAppSettingsState => ({
        goAppConfig: {},
        saveStatus: {},
    }),
    actions: {
        startLoadingGoAppConfig(payload: { rpName: string }) {
            mutateCommonEntityStartState<GoAppConfig>(this, 'goAppConfig', payload);
        },
        finishLoadingGoAppConfig(payload: { rpName: string, data: GoAppConfig }) {
            mutateCommonEntityFinishState<GoAppConfig>(this, 'goAppConfig', payload);
        },
        failLoadingGoAppConfig(payload: { rpName: string }) {
            mutateCommonEntityFailState<GoAppConfig>(this, 'goAppConfig', payload);
        },
        startSavingGoAppConfig(payload: { rpName: string }) {
            this.saveStatus = {
                ...this.saveStatus,
                [payload.rpName]: { status: OperationStatus.loading, error: null },
            };
        },
        finishSavingGoAppConfig(payload: { rpName: string, data: GoAppConfig }) {
            this.saveStatus = {
                ...this.saveStatus,
                [payload.rpName]: { status: OperationStatus.success, error: null },
            };
            this.finishLoadingGoAppConfig(payload);
        },
        // A failed save is answered by the page, so it must not outlive the edit that caused it:
        // left in state it reappears on the next visit, over a form that has since been fixed.
        clearSaveStatus(payload: { rpName: string }) {
            const { [payload.rpName]: _cleared, ...rest } = this.saveStatus;
            this.saveStatus = rest;
        },
        failSavingGoAppConfig(payload: { rpName: string, error: any }) {
            this.saveStatus = {
                ...this.saveStatus,
                [payload.rpName]: { status: OperationStatus.error, error: payload.error },
            };
        },
        // A failure sets the error status so the settings page renders its own inline error state,
        // but auth failures (expired session, revoked access) still go to the persisting-error
        // screen the way every other store sends them, so the user is re-authenticated.
        async loadGoAppConfig(payload: { rpName: string }) {
            const { rpName } = payload;
            const statusOf = (): OperationStatus | undefined => this.goAppConfig[rpName]?.status;
            if (statusOf() === OperationStatus.loading) {
                return;
            }
            this.startLoadingGoAppConfig({ rpName });
            try {
                const data = await appOf(this).gatewayClient.tprmUi().getGoAppConfig(rpName);
                // A save can finish while this load is in flight; keep the newer saved config.
                if (statusOf() !== OperationStatus.loading) {
                    return;
                }
                this.finishLoadingGoAppConfig({ rpName, data });
            } catch (error) {
                this.failLoadingGoAppConfig({ rpName });
                // The GET never answers 400 — it declares 401/403/404 and gives the default config
                // when nothing is saved — so only the auth failures belong on the global screen that
                // re-authenticates. Everything else is the page's own inline "try again later".
                const httpStatus = getHttpStatus(error);
                if (httpStatus === 401 || httpStatus === 403) {
                    await getPersistingErrorActions().showError(error);
                }
            }
        },
        async saveGoAppConfig(payload: { rpName: string, config: GoAppConfig }) {
            const { rpName, config } = payload;
            if (this.saveStatus[rpName]?.status === OperationStatus.loading) {
                return;
            }
            this.startSavingGoAppConfig({ rpName });
            try {
                const data = await appOf(this).gatewayClient.tprmUi().updateGoAppConfig(rpName, config);
                this.finishSavingGoAppConfig({ rpName, data });
            } catch (error) {
                // Kept in state: 400s are user-fixable validation results the form renders inline.
                // Anything else (expired session, unauthorized) goes to the persisting-error screen.
                this.failSavingGoAppConfig({ rpName, error });
                if (getHttpStatus(error) !== 400) {
                    await getPersistingErrorActions().showError(error);
                }
            }
        },
    },
});
